import { createContext } from 'react';
import { UseQueryResult } from '@tanstack/react-query';

import { ContentType, LayoutEntityType } from '@models/Content';
import { LogicalOperator } from '@models/FilterDefinition';
import LayoutEntity from '@models/LayoutEntity';

/**
 * All attributes related to tags (if applicable)
 */
type TagValues = {
  removeTag: (value: string) => void;
  setTagCombinator: (value: LogicalOperator) => void;
  setTags: (value: string[]) => void;
  tagCombinator: LogicalOperator;
  tags: string[];
  tagsLoad: UseQueryResult<string[] | undefined>;
};

export type TypeAttributes<T> = {
  getEntityType: (entity: T) => string;
  /**
   * Translation key
   */
  tKey: string;
  typeOptions: readonly ContentType[];
  types: ContentType[];
};

/**
 * All attributes related to types (if applicable)
 */
type TypeValues<T> = TypeAttributes<T> & {
  removeType: (type: ContentType) => void;
  setTypes: (types: ContentType[]) => void;
};

export type LayoutEntityFilterValues<T extends LayoutEntity> = {
  addEntity: (entity: T) => void;
  clearFilterChips: VoidFunction;
  entities: T[];
  entityType: LayoutEntityType;
  error: Error | null;
  favoriteTags: string[];
  filteredEntities: T[];
  hasFilters: boolean;
  loading: boolean;
  queryText: string;
  removeLayoutEntity: (id: string) => void;
  setQueryText: (value: string) => void;
  tagValues?: TagValues;
  toggleFavoriteTag: (tag: string, isFavorite: boolean) => void;
  typeValues?: TypeValues<T>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LayoutEntityFilterContext = createContext<LayoutEntityFilterValues<any>>({
  addEntity: () => void 0,
  clearFilterChips: () => void 0,
  entities: [],
  entityType: '' as LayoutEntityType,
  error: null,
  favoriteTags: [],
  filteredEntities: [],
  hasFilters: false,
  loading: true,
  queryText: '',
  removeLayoutEntity: () => void 0,
  setQueryText: () => void 0,
  toggleFavoriteTag: () => void 0,
});

export default LayoutEntityFilterContext;
